import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Calendar, Bell, Search, Plus } from 'lucide-react';

type EmptyStateType = 'notes' | 'events' | 'reminders' | 'search' | 'generic';

interface EmptyStateProps {
    /** Type of empty state to display */
    type?: EmptyStateType;
    /** Custom title (overrides default) */
    title?: string;
    /** Custom message (overrides default) */
    message?: string;
    /** Optional action button */
    actionLabel?: string;
    /** Action button click handler */
    onAction?: () => void;
    /** Additional CSS classes */
    className?: string;
}

const EMPTY_STATE_CONFIGS: Record<EmptyStateType, { icon: React.ReactNode; title: string; message: string }> = {
    notes: {
        icon: <FileText className="w-12 h-12" />,
        title: 'Chưa có ghi chú',
        message: 'Nhấn nút + để tạo ghi chú đầu tiên cho ngày này.',
    },
    events: {
        icon: <Calendar className="w-12 h-12" />,
        title: 'Không có sự kiện',
        message: 'Chưa có sự kiện nào trong tháng này.',
    },
    reminders: {
        icon: <Bell className="w-12 h-12" />,
        title: 'Không có nhắc nhở',
        message: 'Bạn chưa thiết lập nhắc nhở nào.',
    },
    search: {
        icon: <Search className="w-12 h-12" />,
        title: 'Không tìm thấy kết quả',
        message: 'Thử tìm kiếm với từ khóa khác.',
    },
    generic: {
        icon: <FileText className="w-12 h-12" />,
        title: 'Không có dữ liệu',
        message: 'Không có nội dung để hiển thị.',
    },
};

/**
 * EmptyState component - Displays friendly empty state messages
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
    type = 'generic',
    title,
    message,
    actionLabel,
    onAction,
    className = '',
}) => {
    const config = EMPTY_STATE_CONFIGS[type];
    const displayTitle = title || config.title;
    const displayMessage = message || config.message;

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className={`flex flex-col items-center justify-center py-8 px-4 text-center ${className}`}
        >
            {/* Icon */}
            <div className="text-gray-400/60 mb-4">
                {config.icon}
            </div>

            {/* Title */}
            <h3 className="text-lg font-semibold text-gray-600 mb-2">
                {displayTitle}
            </h3>

            {/* Message */}
            <p className="text-sm text-gray-400 max-w-xs mb-4">
                {displayMessage}
            </p>

            {/* Action Button */}
            {actionLabel && onAction && (
                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onAction}
                    className="
                        flex items-center gap-2
                        px-4 py-2
                        bg-blue-500 hover:bg-blue-600
                        text-white text-sm font-medium
                        rounded-full
                        shadow-md hover:shadow-lg
                        transition-colors
                    "
                >
                    <Plus className="w-4 h-4" />
                    {actionLabel}
                </motion.button>
            )}
        </motion.div>
    );
};

/**
 * EmptyState for dark backgrounds
 */
export const EmptyStateDark: React.FC<EmptyStateProps> = (props) => {
    return (
        <EmptyState
            {...props}
            className={`
                ${props.className || ''}
                [&_h3]:text-slate-300
                [&_p]:text-slate-500
                [&>div:first-child]:text-slate-600
            `}
        />
    );
};

export default EmptyState;
